import {
  AccessTimeOutlined,
  ApartmentOutlined,
  ChatOutlined,
  CheckRounded,
  CloseRounded,
  ContentCopyOutlined,
  EditOutlined,
  GppMaybeOutlined,
  Inventory2Outlined,
  PlaceOutlined,
  TimerOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import { vehicleScopeKey } from 'src/components/dispatch/vehicleRowScope';
import { DispatchCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { resolveLocationBadge } from './locationBadge';
import { ShipperBreakdownSection } from './ShipperBreakdownSection';
import { DetailPanelMoveTarget } from './types';
import { buildVehicleInfoRows } from './vehicleInfoRows';

export type DispatchSelection =
  | {
      source: 'vehicle';
      card: DispatchCard;
      vehicle: Vehicle;
      orderIndex: number;
    }
  | {
      source: 'unassigned';
      item: UnassignedItem;
    }
  | null;

export interface GeoUpdatePayload {
  newGeo: { lat: number; lng: number };
  address?: string;
  /** loading (積地) / unloading (降地) どちらの waypoint を更新するか */
  side: 'loading' | 'unloading';
  /** 対象 legRun ID (同一カードにマージされた全 legRun の ID を渡す) */
  legRunIds: number[];
}

interface DispatchDetailPanelProps {
  selection: DispatchSelection;
  /** GA の screen_name。GeoDisplay まで渡す */
  screenName: string;
  hasDuplicate?: boolean;
  geoAlert?: GeoAlertInfo;
  onGeoUpdate?: (payload: GeoUpdatePayload) => void;
  /** 指定時は座標編集ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #6）。 */
  disabledReason?: string;
  /**
   * 荷主別内訳から他車両へ移す (logposeTech/log_web_issues#326) ときの移動先候補。
   * 出し分けの判定は器 (DetailPanelShell) が持つので、ここでは渡された分だけ描く。
   */
  moveTargets?: DetailPanelMoveTarget[];
  /** 未指定なら内訳は従来どおり読み取り専用 */
  onMoveShippers?: (legRunIds: number[], targetRowId: string) => void;
}

function SectionRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          height: 20,
          width: 20,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          color: 'hsl(var(--muted-foreground))',
          mt: '2px',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, fontSize: 14, lineHeight: 1.6, color: 'hsl(var(--foreground))' }}>
        {children}
      </Box>
    </Box>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
      {items.map((item) => (
        <Box
          key={item}
          component="span"
          sx={{
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'hsl(var(--border) / 0.6)',
            bgcolor: 'hsl(var(--muted) / 0.4)',
            px: 1,
            py: '2px',
            fontSize: 12,
            color: 'hsl(var(--foreground) / 0.7)',
          }}
        >
          {item}
        </Box>
      ))}
    </Box>
  );
}

function GeoDisplay({ lat, lng, disabled, disabledReason, onGeoUpdate, screenName }: {
  lat: number;
  lng: number;
  /** 座標編集ボタン自体を disabled にする (legRun と紐付かないカード向け) */
  disabled?: boolean;
  /** 指定時はボタンを描いた上で disabled にし、素の title 属性で理由を出す（Task 10 #6）。 */
  disabledReason?: string;
  onGeoUpdate?: (newGeo: { lat: number; lng: number }) => void;
  screenName: string;
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const geoText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    ReactGA.event('click', {
      screen_name: screenName,
      button_name: '座標コピー',
    });
    navigator.clipboard
      .writeText(geoText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* ignore */
      });
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabledReason) return;
    ReactGA.event('click', {
      screen_name: screenName,
      button_name: '座標編集',
    });
    setEditValue(geoText);
    setInvalid(false);
    setEditing(true);
  };

  const parseInput = (): { lat: number; lng: number } | null => {
    const parts = editValue.split(',').map((s) => s.trim());
    if (parts.length !== 2) return null;
    const newLat = parseFloat(parts[0]);
    const newLng = parseFloat(parts[1]);
    if (Number.isNaN(newLat) || Number.isNaN(newLng)) return null;
    if (Math.abs(newLat) > 90 || Math.abs(newLng) > 180) return null;
    return { lat: newLat, lng: newLng };
  };

  const handleConfirmEdit = () => {
    const parsed = parseInput();
    if (!parsed) {
      setInvalid(true);
      return;
    }
    ReactGA.event('apply', {
      screen_name: screenName,
      button_name: '座標編集',
    });
    onGeoUpdate?.(parsed);
    setInvalid(false);
    setEditing(false);
  };

  const handleCancelEdit = () => {
    ReactGA.event('close', {
      screen_name: screenName,
      button_name: '座標編集',
    });
    setInvalid(false);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleConfirmEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  if (editing) {
    return (
      <Box
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <PlaceOutlined sx={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }} />
        <Box
          component="input"
          ref={inputRef}
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEditValue(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={handleKeyDown}
          placeholder="lat, lng"
          sx={{
            width: 180,
            borderRadius: '4px',
            border: '1px solid',
            borderColor: invalid ? 'hsl(var(--status-error-accent))' : 'hsl(var(--border))',
            bgcolor: 'hsl(var(--background))',
            px: 0.75,
            py: '2px',
            fontSize: 10,
            fontFamily: 'monospace',
            color: 'hsl(var(--foreground))',
            outline: 'none',
            '&:focus': {
              boxShadow: `0 0 0 1px ${invalid ? 'hsl(var(--status-error-accent))' : 'hsl(var(--ring))'}`,
            },
          }}
        />
        <Tooltip title="確定 (Enter)" placement="top" enterDelay={200}>
          <Box
            component="button"
            type="button"
            onClick={handleConfirmEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 18,
              width: 18,
              borderRadius: '4px',
              color: 'hsl(var(--status-success-foreground))',
              bgcolor: 'hsl(var(--status-success-accent) / 0.12)',
              border: '1px solid hsl(var(--status-success-accent) / 0.4)',
              cursor: 'pointer',
              p: 0,
              transition: 'background-color 150ms',
              '&:hover': { bgcolor: 'hsl(var(--status-success-accent) / 0.22)' },
            }}
          >
            <CheckRounded sx={{ fontSize: 12 }} />
          </Box>
        </Tooltip>
        <Tooltip title="キャンセル (Esc)" placement="top" enterDelay={200}>
          <Box
            component="button"
            type="button"
            onClick={handleCancelEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 18,
              width: 18,
              borderRadius: '4px',
              color: 'hsl(var(--muted-foreground))',
              bgcolor: 'hsl(var(--muted) / 0.4)',
              border: '1px solid hsl(var(--border))',
              cursor: 'pointer',
              p: 0,
              transition: 'background-color 150ms',
              '&:hover': { bgcolor: 'hsl(var(--muted) / 0.7)' },
            }}
          >
            <CloseRounded sx={{ fontSize: 12 }} />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', ml: 0.5 }}>
      <Tooltip title={copied ? 'コピーしました' : 'クリックしてコピー'} placement="top" enterDelay={200}>
        <Box
          component="button"
          type="button"
          onClick={handleCopy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 10,
            fontFamily: 'monospace',
            color: 'hsl(var(--muted-foreground))',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            transition: 'color 150ms',
            '&:hover': { color: 'hsl(var(--foreground))' },
          }}
        >
          <PlaceOutlined sx={{ fontSize: 12 }} />
          <span>{geoText}</span>
          {copied
            ? <CheckRounded sx={{ fontSize: 12, color: 'hsl(var(--status-success-accent))' }} />
            : <ContentCopyOutlined sx={{ fontSize: 12 }} />}
        </Box>
      </Tooltip>
      {!disabled && onGeoUpdate && (
        /* 押せない理由は Tooltip に載せる。素の title を Tooltip の直下の子に付けると
           MUI が警告を出す（Tooltip.js が children.props.title を見ている）。disabled な
           要素は hover を拾わないので span で包む。describeChild にするのは、読み上げ名を
           ボタン自身の aria-label に保つため（既定では Tooltip の文言が子の aria-label に
           なり、理由が出ているときに名前ごと変わってしまう）。 */
        <Tooltip title={disabledReason || '座標を編集'} placement="top" enterDelay={200} describeChild>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Box
              component="button"
              type="button"
              // Tooltip の文言は理由で変わるので、読み上げ名はボタン自身に持たせる
              // （Tooltip 任せだと disabled のとき名前が理由の文言に変わる）
              aria-label="座標を編集"
              onClick={handleStartEdit}
              disabled={!!disabledReason}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 16,
                width: 16,
                color: 'hsl(var(--muted-foreground))',
                background: 'transparent',
                border: 'none',
                cursor: disabledReason ? 'default' : 'pointer',
                opacity: disabledReason ? 0.5 : 1,
                p: 0,
                transition: 'color 150ms',
                '&:hover': disabledReason ? undefined : { color: 'hsl(var(--foreground))' },
              }}
            >
              <EditOutlined sx={{ fontSize: 10 }} />
            </Box>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

const dividerSx = { borderTop: '1px solid', borderColor: 'hsl(var(--border) / 0.4)' } as const;

export function DispatchDetailPanel({
  selection, screenName, hasDuplicate = false, geoAlert, onGeoUpdate, disabledReason,
  moveTargets, onMoveShippers,
}: DispatchDetailPanelProps) {
  if (!selection) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'hsl(var(--border))',
          bgcolor: 'hsl(var(--secondary) / 0.3)',
          px: 3,
          py: 5,
          textAlign: 'center',
          fontSize: 14,
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        カードを選択すると詳細を確認できます。
      </Box>
    );
  }

  const title =
    selection.source === 'vehicle'
      ? selection.card.type === 'loading'
        ? selection.card.locationName ?? '積み込み拠点'
        : selection.card.storeName
      : selection.item.storeName;

  const vehicleInfoRows = buildVehicleInfoRows(selection.source === 'vehicle' ? selection.vehicle : null);

  const locationBadge = resolveLocationBadge(
    selection.source === 'vehicle'
      ? { source: 'vehicle', cardType: selection.card.type === 'delivery' ? 'delivery' : 'loading' }
      : { source: 'unassigned' },
  );

  const card = selection.source === 'vehicle' ? selection.card : null;
  const deliveryCard = card?.type === 'delivery' ? card : null;
  const loadingCard = card?.type === 'loading' ? card : null;
  const unassignedItem = selection.source === 'unassigned' ? selection.item : null;

  const address = deliveryCard?.storeAddress ?? loadingCard?.address ?? unassignedItem?.storeAddress ?? undefined;
  const geo = deliveryCard?.geo ?? loadingCard?.geo ?? unassignedItem?.geo ?? undefined;

  // 座標編集の対象 side と legRunIds を決定する
  // - delivery カード (降地): side=unloading + card.legRunIds
  // - loading カード (積地):   side=loading + card.legRunIds
  // - unassigned / no-delivery / fr-template 等: 編集不可
  const geoEditSide: 'loading' | 'unloading' | null = deliveryCard
    ? (deliveryCard.status === 'matched' || deliveryCard.status === 'template' ? 'unloading' : null)
    : loadingCard
      ? 'loading'
      : null;
  const geoEditLegRunIds: number[] = geoEditSide === 'unloading'
    ? (deliveryCard?.legRunIds ?? [])
    : geoEditSide === 'loading'
      ? (loadingCard?.legRunIds ?? [])
      : [];
  const canEditGeo = geoEditSide != null && geoEditLegRunIds.length > 0;

  const scheduledDate = deliveryCard?.scheduledDate ?? loadingCard?.scheduledDate ?? unassignedItem?.scheduledDate ?? '';
  const scheduledTime = deliveryCard?.scheduledTime ?? loadingCard?.scheduledTime ?? unassignedItem?.scheduledTime ?? '';
  const workDuration = deliveryCard?.workDuration ?? loadingCard?.workDuration ?? unassignedItem?.workDuration ?? null;

  const cases = deliveryCard?.todayCases ?? deliveryCard?.cases ?? loadingCard?.cases ?? unassignedItem?.cases ?? null;
  const weight = deliveryCard?.weight ?? loadingCard?.weight ?? unassignedItem?.weight ?? null;
  const volume = deliveryCard?.volume ?? loadingCard?.volume ?? unassignedItem?.volume ?? null;
  const invoiceCount = deliveryCard?.invoiceCount ?? loadingCard?.invoiceCount ?? unassignedItem?.invoiceCount ?? null;

  const remarks = deliveryCard?.remarks ?? loadingCard?.remarks ?? unassignedItem?.remarks ?? null;
  const shippers = deliveryCard?.shippers ?? loadingCard?.shippers ?? unassignedItem?.shippers ?? [];
  // 内訳は複数の荷主が混ざるときだけ。1 社なら合計と同じ数字が並ぶだけになる
  const shipperBreakdown = deliveryCard?.shipperBreakdown ?? unassignedItem?.shipperBreakdown ?? [];
  const showShipperBreakdown = shipperBreakdown.length >= 2;
  /**
   * 内訳の選択状態を捨てる単位。別のカードへ切り替わったら React に作り直させる
   * （残ると別カードの荷主を選んだつもりで実行される）。
   *
   * Why 勤務 (vehicleScopeKey) まで入れる: 積地カードの id は勤務をまたいで衝突し、
   * 積地の orderIndex は常に 0 なので、id と orderIndex だけでは別勤務の同じ位置の
   * カードを同じものと見なしてしまう (vehicleRowScope.ts の Why / #320)。
   */
  const breakdownKey = selection.source === 'vehicle'
    ? `vehicle:${vehicleScopeKey(selection.vehicle)}:${selection.card.id}:${selection.orderIndex}`
    : `unassigned:${selection.item.id}`;
  const designatedTimeWindow = deliveryCard?.designatedTimeWindow ?? loadingCard?.designatedTimeWindow ?? unassignedItem?.designatedTimeWindow ?? null;
  const constraints = deliveryCard?.constraints ?? loadingCard?.constraints ?? unassignedItem?.constraints ?? [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ pb: 1.5 }}>
        {locationBadge && (
          <Box
            component="span"
            data-testid="detail-location-badge"
            sx={{
              display: 'inline-block',
              fontSize: 9,
              fontWeight: 700,
              color: locationBadge.color,
              px: 0.5,
              py: '1px',
              mb: 0.5,
              borderRadius: '4px',
              border: '1px solid',
              borderColor: locationBadge.borderColor,
              bgcolor: locationBadge.bgColor,
            }}
          >
            {locationBadge.label}
          </Box>
        )}
        <Box sx={{ fontSize: 16, fontWeight: 600, color: 'hsl(var(--foreground))', lineHeight: 1.3 }}>{title}</Box>
        {address && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, mt: '2px' }}>
            <Box component="span" sx={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.7)' }}>
              {address}
            </Box>
            {geo && (
              <GeoDisplay
                lat={geo.lat}
                lng={geo.lng}
                disabled={!canEditGeo}
                disabledReason={disabledReason}
                onGeoUpdate={canEditGeo && geoEditSide
                  ? (newGeo) => onGeoUpdate?.({ newGeo, address, side: geoEditSide, legRunIds: geoEditLegRunIds })
                  : undefined}
                screenName={screenName}
              />
            )}
          </Box>
        )}
        {!address && geo && (
          <Box sx={{ mt: '2px' }}>
            <GeoDisplay
              lat={geo.lat}
              lng={geo.lng}
              disabled={!canEditGeo}
              disabledReason={disabledReason}
              onGeoUpdate={canEditGeo && geoEditSide
                ? (newGeo) => onGeoUpdate?.({ newGeo, side: geoEditSide, legRunIds: geoEditLegRunIds })
                : undefined}
              screenName={screenName}
            />
          </Box>
        )}
        {geoAlert && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.75,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              fontSize: 11,
              lineHeight: 1.2,
              bgcolor:
                geoAlert.level === 'strong'
                  ? 'hsl(var(--destructive) / 0.08)'
                  : 'hsl(var(--status-warning-accent) / 0.08)',
              color:
                geoAlert.level === 'strong'
                  ? 'hsl(var(--status-error-accent))'
                  : 'hsl(var(--status-warning-foreground))',
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 12, flexShrink: 0 }} />
            <span>{geoAlert.message}</span>
          </Box>
        )}
        {vehicleInfoRows.length > 0 && (
          <Box
            data-testid="detail-vehicle-info"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 1,
              rowGap: '2px',
              fontSize: 12,
              color: 'hsl(var(--foreground) / 0.72)',
              mt: 0.75,
            }}
          >
            {vehicleInfoRows.map((row) => (
              <Fragment key={row.label}>
                <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>{row.label}</Box>
                <Box component="span">{row.value}</Box>
              </Fragment>
            ))}
          </Box>
        )}
      </Box>

      {/* Schedule */}
      {(scheduledDate || scheduledTime) && (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, ...dividerSx }}>
          <Box
            sx={{
              display: 'flex',
              height: 20,
              width: 20,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              color: 'hsl(var(--muted-foreground))',
              mt: '2px',
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ fontSize: 14, color: 'hsl(var(--foreground))', lineHeight: 1.6 }}>
            {scheduledDate && <span>{scheduledDate}</span>}
            {scheduledDate && scheduledTime && <Box component="span" sx={{ mx: 0.5 }}>⋅</Box>}
            {scheduledTime && <Box component="span" sx={{ fontWeight: 500 }}>{scheduledTime}</Box>}
            {workDuration != null && (
              <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.6)', ml: 0.75 }}>
                作業時間{workDuration}分
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={dividerSx} />

      <SectionRow icon={<Inventory2Outlined sx={{ fontSize: 16 }} />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.5 }}>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>数量</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{cases != null ? `${quantityValueText(cases)} cs` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>重量</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{weight != null ? `${weightValueText(weight)} kg` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>体積</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{volume != null ? `${volumeValueText(volume)} m³` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>伝票枚数</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{invoiceCount != null ? `${invoiceCount} 枚` : '--'}</Box>
          </Box>
        </Box>
      </SectionRow>

      {/* 積地カードでは備考・荷主名は情報量が多くなりがちなので非表示 */}
      {!loadingCard && (
        <>
          <Box sx={dividerSx} />

          <SectionRow icon={<ChatOutlined sx={{ fontSize: 16 }} />}>
            <Box component="span" sx={!remarks ? { color: 'hsl(var(--muted-foreground))' } : undefined}>
              {remarks || '備考なし'}
            </Box>
          </SectionRow>

          <Box sx={dividerSx} />

          <SectionRow icon={<ApartmentOutlined sx={{ fontSize: 16 }} />}>
            {showShipperBreakdown ? (
              // 同一地点に複数の荷主が混ざると、合計だけでは誰のぶんが何個か読めない
              <ShipperBreakdownSection
                key={breakdownKey}
                rows={shipperBreakdown}
                // 未割当に「他車両へ移動」は無い。出しても押した先 (moveToVehicle) で
                // 必ず弾かれるので、そもそも選ばせない
                moveTargets={selection.source === 'vehicle' ? moveTargets : undefined}
                onMoveShippers={selection.source === 'vehicle' ? onMoveShippers : undefined}
              />
            ) : shippers.length > 0 ? (
              <TagList items={shippers} />
            ) : (
              <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>荷主情報なし</Box>
            )}
          </SectionRow>
        </>
      )}

      <Box sx={dividerSx} />

      <SectionRow icon={<TimerOutlined sx={{ fontSize: 16 }} />}>
        <Box>
          <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>指定時間：</Box>
          <Box component="span" sx={{ fontWeight: 500, fontSize: 14 }}>
            {designatedTimeWindow ? designatedTimeWindow : 'なし'}
          </Box>
        </Box>
      </SectionRow>

      <Box sx={dividerSx} />

      <SectionRow icon={<GppMaybeOutlined sx={{ fontSize: 16 }} />}>
        {constraints.length > 0 ? (
          <TagList items={constraints} />
        ) : (
          <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>制約なし</Box>
        )}
        {hasDuplicate && selection.source === 'vehicle' && selection.card.type === 'delivery' && (
          <Box sx={{ mt: 0.75, fontSize: 12, color: 'hsl(var(--destructive))' }}>
            ⚠ 同一納品先が他車両にも割り当て済み
          </Box>
        )}
      </SectionRow>
    </Box>
  );
}
